<template>
    <tr>
        <div class="responsiveTD dynamic4ColExpand" @click="toggleCollapse">
            <td data-label="Platform Name">
                {{name}}
            </td>
            <td data-label="Type">
                {{type}}
            </td>
            <td data-label="Location">
                {{location}}
            </td>
            <td data-label="Lease Number">
                <router-link :to="{name: 'Leases', params: {lease: leaseNum}}" class="link">{{leaseNum}}</router-link>
            </td>
            <button data-label="More Information" v-show="expandable" style="cursor: pointer;">
                <svg v-if="!isCollapsed" width="24" height="24">
                    <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"></path>
                </svg>
                <svg v-else width="24" height="24">
                    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"></path>
                </svg>
            </button>
        </div>
        <td class="expandableTableRow" v-show="expandable" :style="currHeight" style="padding: 0;">
            <div class="expandableTableContent">
                <div class="info2Cols">
                    <div class="category" v-if="general.length > 0" :class="{info2ColsFullRow: details.length == 0}">
                        <h6 class="categoryTitle">General</h6>
                        <div v-for="obj in general" :key="obj.field" class="dataItem">
                            <p>{{obj.field}}: <span class="dataEmphasis">{{obj.val}}</span></p>
                        </div>
                    </div>
                    <div class="category" v-if="details.length > 0" :class="{info2ColsFullRow: general.length == 0}">
                        <h6 class="categoryTitle">Details</h6>
                        <div v-for="obj in details" :key="obj.field" class="dataItem">
                            <p v-if="obj.val != undefined">{{obj.field}}: <span class="dataEmphasis">{{obj.val}}</span></p>
                            <p v-else><span class="dataEmphasis">{{obj.field}}</span></p>
                        </div>
                    </div>
                </div>
                <div class="category" v-if="vessels.length > 0">
                    <div class="categoryTitle titleWithExpand" @click="toggleVesselCollapse">
                        <h5>Vessels Visited</h5>
                        <button style="cursor: pointer;">
                            <svg v-if="!isVesselCollapsed" width="24" height="24">
                                <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"></path>
                            </svg>
                            <svg v-else width="24" height="24">
                                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"></path>
                            </svg>
                        </button>
                    </div>
                    <div :style="currVesselHeight" class="expandableTableRow" v-if="!isCollapsed">
                        <table class="vesselTable">
                            <thead>
                                <tr>
                                    <th scope="col"> Vessel </th>
                                    <th scope="col"> Arrival Time </th>
                                    <th scope="col"> Departure Time </th>
                                    <th scope="col"> Minutes Elapsed </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="obj in vessels" :key="obj.ingress">
                                    <td data-label="Vessel"><p class="vesselIdentify"><router-link :to="{name: 'Vessels', params: { mmsi: obj.mmsi }}" class="link">{{obj.mmsi}}</router-link> {{obj.name}}</p></td>
                                    <td data-label="Arrival Time" v-if="useUTC">{{obj.ingressUTC}}</td>
                                    <td data-label="Arrival Time" v-else>{{obj.ingressLT}}</td>
                                    <td data-label="Departure Time" v-if="useUTC && obj.egressUTC">{{obj.egressUTC}}</td>
                                    <td data-label="Departure Time" v-else-if="obj.egressLT">{{obj.egressLT}}</td>
                                    <td data-label="Departure Time" v-else> — </td>
                                    <td data-label="Minutes Elapsed">{{obj.minuteDelta}}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </td>
    </tr>
</template>

<script>
import { readFromObj, readFromObjBool, formatDateLT, formatDateUTC } from '../../helpers/Formatting.js';

export default {
    name: 'op-platform-item',
    props: [
        "platformItem"
    ],
    data: () => ({
        name: null,
        type: null,
        location: null,
        leaseNum: null,
        general: [],
        details: [],
        vessels: [],
        expandable: false,
        isCollapsed: true,
        expandableHeight: 0,
        isVesselCollapsed: true,
        vesselHeight: 0,
        useUTC: false,
    }),
    created() {
        var globv = this;
        globv.name = globv.platformItem['name'];
        globv.type = globv.platformItem['type'];
        globv.location = globv.platformItem['location'];
        globv.leaseNum = globv.platformItem['leaseNum'];

        let expectedVariables = [
            {
                varName: "typeDesc",
                fieldName: "Type Description"
            },
            {
                varName: "installDate",
                fieldName: "Install Date",
                format: formatDateLT
            },
            {
                varName: "revisionDate",
                fieldName: "Revision Date",
                format: formatDateLT
            },
            {
                varName: "fieldName",
                fieldName: "Field Name"
            },
            {
                varName: "waterDepth",
                fieldName: "Water Depth",
                unit: " m"
            },
        ]

        globv.general = readFromObj(globv.platformItem, expectedVariables);

        if (globv.platformItem['coordinates'] && globv.platformItem['coordinates'].length === 2) {
            let lat = globv.platformItem['coordinates'][1];
            let lon = globv.platformItem['coordinates'][0];
            if (lat !== null && lon !== null) {
                globv.general.push({
                    field: "Latitude / Longitude",
                    val: lat + "° / " + lon + "°"
                });
            }
        }

        expectedVariables = [
            {
                varName: "numCranes",
                fieldName: "Number of Cranes"
            },
            {
                varName: "numDecks",
                fieldName: "Number of Decks"
            }
        ]

        globv.details = readFromObj(globv.platformItem, expectedVariables);

        expectedVariables = [
            {
                varName: "isMajorStruct",
                fieldName: "Is a major structure"
            },
            {
                varName: "isManned247",
                fieldName: "Manned 24/7"
            },
            {
                varName: "hasHeliport",
                fieldName: "Has a heliport"
            },
            {
                varName: "hasFlareStacks",
                fieldName: "Has flare stacks"
            },
        ]

        globv.details.concat(readFromObjBool(globv.platformItem, expectedVariables));

        globv.vessels = globv.platformItem['vessels'];
        if (globv.vessels && globv.vessels.length > 0) {
            globv.vessels = globv.vessels.map(x => {
                x['ingressLT'] = formatDateLT(x['ingress']);
                x['ingressUTC'] = formatDateUTC(x['ingress']);
                if (x['egress']) {
                    x['egressLT'] = formatDateLT(x['egress']);
                    x['egressUTC'] = formatDateUTC(x['egress']);
                }
                if (!x['minuteDelta']) {
                    x['minuteDelta'] = Math.floor((Date.now() - x['ingress']) / 60000);
                }
                return x;
            });
        }

        globv.expandable = globv.general.length > 0 || globv.details.length > 0 || globv.vessels.length > 0;
        globv.expandableHeight = (globv.general.length + globv.details.length ) * 24 + 48;
        globv.vesselHeight = globv.vessels.length * 56 + 57 + 24;
    },
    methods: {
        toggleCollapse: function() {
            var globv = this;
            if (globv.expandable)
                globv.isCollapsed = !globv.isCollapsed;
        },
        toggleVesselCollapse: function() {
            var globv = this;
            globv.isVesselCollapsed = !globv.isVesselCollapsed;
        }
    },
    computed: {
        currHeight() {
            var globv = this;

            if (globv.expandable) {
                let height = globv.expandableHeight + globv.vesselHeight;
                return {
                    "max-height": globv.isCollapsed ? "0px" : height * 20 + 'px'
                }
            }
            
            return {};
        },
        currVesselHeight() {
            var globv = this;
            
            if (globv.vessels.length > 0) {
                return {
                    "max-height": globv.isVesselCollapsed ? "0px" : globv.vesselHeight * 20 + 'px'
                }
            }

            return {};
        }
    }
}
</script>

<style lang="scss" scoped>
.vesselTable {
    border-collapse: collapse;
    text-align: center;
    width: 100%;
    justify-self: center;
    border: 1px solid grey;

    thead tr {
        border-bottom: lightgrey 1px solid;
    }

    th, td {
        padding: 10px 18px;
    }

    tr:nth-child(even) {
        background-color: #f2f2f2;
        background-color: var(--highlightBackground);
    }
}

@media (max-width: 650px) {
    .vesselTable {
        border: none;
        
        thead {
            border: none;
            height: 1px;
            width: 1px;
            margin: -1px;
            overflow: hidden;
            padding: 0;
            position: absolute;
        }

        tr {
            display: grid;
            margin-bottom: 10px;
            border: 1px solid lightgray;
            border-color: var(--secondaryBorderColor);

            &:nth-child(even) {
                background-color: #DEDEE3;
                background-color: var(--tableDarkColor);
            }
        }

        td {
            border-bottom: 1px dotted lightgray;
            border-bottom-color: var(--secondaryBorderColor);
            display: grid;
            grid-template-columns: 40% 1fr;
            column-gap: 10px;
            font-weight: bold;
            text-align: start;

            &:last-child {
                border-bottom: none;
            }

            &::before {
                content: attr(data-label);
                float: left;
                font-weight: normal;
                text-align: left;
            }
        }
    }
}

@media (max-width: 445px) {
    .vesselTable td {
        grid-template-columns: 1fr;
    }
}

</style>